import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import { SkillComparisonReport } from './skill-comparison-report.component';
import { IEnhancedTableColumn, IEnhancedTableRow, StickyTableCell } from '../../_core/_ui/table.components';
import { numToTimeResult, toScore, toWeighted } from '../../../lib/helpers/conversion.helpers';
import TableCell from '@material-ui/core/TableCell';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { SportTerminology } from '../../../lib/types';
import { getPlayerStatus } from '../../../lib/utils/player.utils';
import { getPlayerName } from '../../../lib/utils/player-name.service';
import { Box, Tooltip } from '@material-ui/core';
import { WarningOutlined } from '@material-ui/icons';

interface ISkillComparisonReportContainer {
  skills: ISkill[];
  skillSummaries: IScoreSummary[];
  playerAssessments: IPlayerAssessment[];
  ageGroup?: IAgeGroup;
  fetchError: boolean;
  loading: boolean;
  useMedian: boolean;
  goalieFilter: 'goalie' | 'other';
}

export const SkillComparisonReportContainer: React.FC<ISkillComparisonReportContainer> = ({
  useMedian,
  skills,
  skillSummaries,
  playerAssessments,
  ageGroup,
  loading,
  fetchError,
  goalieFilter
}) => {
  const termLookup = useCompanySportTerminology();
  const athleteTerm = termLookup[SportTerminology.ATHLETE];
  const [reportRows, setReportRows] = useState<IEnhancedTableRow[]>([]);
  const [reportColumns, setReportColumns] = useState<IEnhancedTableColumn[]>([]);

  // Filter playerAssessments by goalieFilter
  const filteredPlayerAssessments = useMemo(() => goalieFilter === 'goalie'
    ? playerAssessments.filter(pa => pa.position?.name?.toLowerCase() === 'goalie')
    : playerAssessments.filter(pa => pa.position?.name?.toLowerCase() !== 'goalie'),
    [playerAssessments, goalieFilter]
  );

  useEffect(() => {
    const rankedPlayers = _.orderBy(filteredPlayerAssessments, playerAssessment => Number(playerAssessment.adjustedScore === "-" ? 0 : playerAssessment.adjustedScore), ['desc']);
    const nextReportRows: any[] = [];
    const skillsById = _.keyBy(skills, '_id');
    const skillSummariesByPlayer: { [key: string]: { [key: string]: IScoreSummary }} = _.reduce(skillSummaries, (result, skillSummary) => {
      _.set(result, [skillSummary.playerAssessmentId, skillSummary.skillId], skillSummary);

      return result;
    }, {});
    let activeSkillIds = _.map(skillSummaries, 'skillId');
    activeSkillIds = _.uniq(activeSkillIds);
    activeSkillIds = _.sortBy(activeSkillIds, skillId => skillsById[skillId].name);

    const nextColumns = [
      { key: 'name', label: '', sticky: true },
      { key: 'warnings', label: '', disableSort: true, width: '60px' },
      { key: 'position', label: 'Position' },
      { key: 'level', label: 'Level' },
      { key: 'team', label: 'Team' },
      { key: 'status', label: termLookup[SportTerminology.ATHLETE] + ' Status', disableSort: true },
      goalieFilter === 'goalie' ? null : { key: 'overallRank', label: 'Overall Rank' },
      { key: 'positionRank', label: 'Position Rank' },
      { key: 'adjusted', label: 'UNBIAS™ Result', path: 'adjustedScore' },
    ].filter(Boolean) as IEnhancedTableColumn[];

    _.each(_.uniq(activeSkillIds), (skillId) => {
      nextColumns.push({ key: skillId, label: skillsById[skillId].name });
    });

    _.each(rankedPlayers, (playerAssessment, index) => {
      let adjustedScore: string | number = '-';

      if (playerAssessment.adjustedScore && playerAssessment.adjustedScore !== '-') {
        adjustedScore = toScore(playerAssessment.adjustedScore || '-');

        adjustedScore = parseFloat(adjustedScore);
        adjustedScore = adjustedScore.toFixed(1);
      }

      const data: { [key: string]: string | number } = {
        name: getPlayerName(playerAssessment),
        team: playerAssessment.team,
        level: playerAssessment.level,
        overallRank: playerAssessment.overallRank ?? '-',
        positionRank: playerAssessment.positionRank ?? '-',
        adjustedScore,
        position: playerAssessment.position.name
      };

      _.each(activeSkillIds, (skillId) => {
        data[skillId] = Number(_.get(skillSummariesByPlayer, [playerAssessment._id, skillId, useMedian ? 'median' : 'mean'], 0));
      });

      const eleSkills = _.map(activeSkillIds, (skillId) => {
        const currSummary = _.get(skillSummariesByPlayer, [playerAssessment._id, skillId]);
        const time = useMedian ? currSummary?.medianTime : currSummary?.meanTime;

        return (
          <TableCell key={skillId}>
            {currSummary && (<>
              <div>{_.round(Number(useMedian ? currSummary.median : currSummary.mean), 2)}{time && `, ${numToTimeResult(time)}`}</div>
            </>)}
            {!currSummary && '-'}
          </TableCell>
        );
      });

      nextReportRows.push({
        _id: playerAssessment._id,
        data: data,
        cells: [
          <StickyTableCell key="name">{getPlayerName(playerAssessment)}</StickyTableCell>,
          <TableCell key="warnings" padding="checkbox">
            <Box display="flex" alignItems="center" justifyContent="center" style={{ padding: '12px' }}>
              {!!playerAssessment?.unappliedActionsCount &&
                <Tooltip title={`Found ${playerAssessment.unappliedActionsCount} new scores`}>
                  <WarningOutlined color="error" />
                </Tooltip>
              }
            </Box>
          </TableCell>,
          <TableCell key="position">{playerAssessment.position.name}</TableCell>,
          <TableCell key="level">{playerAssessment.level}</TableCell>,
          <TableCell key="team">{playerAssessment.team}</TableCell>,
          <TableCell key="status">{getPlayerStatus(playerAssessment)}</TableCell>,
          goalieFilter === 'goalie' ? null : <TableCell key="overallRank">{playerAssessment.overallRank}</TableCell>,
          <TableCell key="positionRank">{playerAssessment.positionRank}</TableCell>,
          <TableCell key="adjustedScore">{adjustedScore}</TableCell>,
          ...eleSkills
        ].filter(Boolean)
      });
    });

    setReportRows(nextReportRows);
    setReportColumns(nextColumns);
  }, [filteredPlayerAssessments]);

  const handleExportReport = (e: any) => {
    e.preventDefault();
    e.stopPropagation();

    exportReports({ ageGroupName: ageGroup?.name || '', metricTerm: termLookup[SportTerminology.METRIC], reportRows, reportColumns, skillSummaries });
  }

  return <SkillComparisonReport
    exportReports={handleExportReport}
    columns={reportColumns}
    reportRows={reportRows}
    fetchError={fetchError}
    loading={loading}
    defaultOrder={goalieFilter === 'goalie' ? 'positionRank' : 'overallRank'}
  />;
};

interface IExportReports {
  ageGroupName: string;
  metricTerm: string
  reportRows: IEnhancedTableRow[];
  reportColumns: IEnhancedTableColumn[];
  skillSummaries: IScoreSummary[];
}

function exportReports({ ageGroupName, metricTerm, reportColumns, reportRows, skillSummaries }: IExportReports) {
  let activeSkillIds = _.map(skillSummaries, 'skillId');
  activeSkillIds = _.uniq(activeSkillIds);

  let csvContent: string = 'data:text/csv;charset=utf-8,';
  let encodedURI;

  _.each(reportColumns, (reportColumn, index) => {
    if (reportColumn.key === 'name') {
      csvContent += 'Name';
    } else {
      csvContent += reportColumn.label;
    }

    if (index < reportColumns.length - 1) {
      csvContent += ',';
    }
  })
  csvContent += '\n';

  _.each(reportRows, ({ data }, index) => {
    let row = '';

    row = `"${data.name}",${data.position},${data.level || ''},${data.team || ''},${data.rank},${toWeighted(data.adjustedScore)}`;
    _.each(activeSkillIds, (skillId) => {
      row += `,"${data[skillId] === 0 ? '-' : _.round(data[skillId], 4)}"`;
    });

    csvContent += index < reportRows.length ? row + '\n' : row;
  });

  encodedURI = encodeURI(csvContent);

  let exportLink = document.createElement("a");
  exportLink.setAttribute("href", encodedURI);
  exportLink.setAttribute("download", `${metricTerm}-comparison-${ageGroupName}.csv`);
  exportLink.setAttribute("target", "_blank");
  document.body.appendChild(exportLink); // Required for FF

  exportLink.click();

  return false;
}

